import {
  api,
  TaskItem,
  TaskStatus,
  Epic,
  Sprint,
  ProjectDetail,
} from '../api';
import { statusMeta, STATUSES } from '../lib/status';
import { escapeHtml, formatDate } from '../lib/utils';
import { flattenTree } from '../lib/taskTree';
import { openTaskModal } from '../components/taskModal';
import { openTaskForm } from '../components/taskForm';
import { toast } from '../components/toast';
import { ProjectContext } from './project';

interface Filters {
  status: string;
  assignee: string;
  epic: string;
  search: string;
}

export async function renderTasks(
  content: HTMLElement,
  ctx: ProjectContext
): Promise<void> {
  const { project, tasks, epics, reload, readOnly } = ctx;
  const editable = !readOnly;

  // Owners can bulk-assign tasks to sprints, so load the sprint list up front.
  let sprints: Sprint[] = [];
  if (editable) {
    try {
      const res = await api.get<{ sprints: Sprint[] }>(
        `/projects/${project.id}/sprints`
      );
      sprints = res.sprints;
    } catch {
      sprints = [];
    }
  }

  const assigneeValues = Array.from(
    new Set(tasks.map((t) => t.assigneeName || 'Unassigned'))
  ).sort();

  const selected = new Set<string>();
  let visible = new Set<string>();

  content.innerHTML = `
    <div class="list-toolbar">
      <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;">
        ${
          editable
            ? `<label class="task-select-all" title="Select all tasks in this view">
                 <input type="checkbox" id="select-all" />
                 <span>All</span>
               </label>`
            : ''
        }
        <select class="select" id="status-filter" style="max-width:160px;">
          <option value="all">All statuses</option>
          ${STATUSES.map((s) => `<option value="${s.key}">${s.label}</option>`).join('')}
        </select>
        <select class="select" id="assignee-filter" style="max-width:180px;">
          <option value="all">All members</option>
          ${assigneeValues
            .map((a) => `<option value="${escapeHtml(a)}">${escapeHtml(a)}</option>`)
            .join('')}
        </select>
        <select class="select" id="epic-filter" style="max-width:180px;">
          <option value="all">All epics</option>
          ${epics
            .map((e) => `<option value="${escapeHtml(e.id)}">${escapeHtml(e.name)}</option>`)
            .join('')}
        </select>
        <input class="input" id="search" placeholder="Search tasks…" />
      </div>
      ${
        readOnly
          ? '<span class="muted" style="font-size:13px;">Read-only view</span>'
          : '<button class="btn btn-primary" id="add-task">+ New Task</button>'
      }
    </div>
    ${
      editable
        ? `
    <div id="bulk-bar" class="bulk-bar hidden">
      <span class="bulk-count" id="bulk-count">0 selected</span>
      <div class="bulk-actions">
        <select class="select" id="bulk-sprint">
          <option value="">Assign to sprint…</option>
          ${sprints
            .map((s) => `<option value="${escapeHtml(s.id)}">${escapeHtml(s.name)}</option>`)
            .join('')}
          <option value="__none__">Remove from sprint</option>
        </select>
        <select class="select" id="bulk-status">
          <option value="">Set status…</option>
          ${STATUSES.map((s) => `<option value="${s.key}">${s.label}</option>`).join('')}
        </select>
        <select class="select" id="bulk-assignee">
          <option value="">Set assignee…</option>
          ${project.members
            .map((m) => `<option value="${escapeHtml(m.id)}">${escapeHtml(m.name)}</option>`)
            .join('')}
          <option value="__none__">Unassigned</option>
        </select>
        <select class="select" id="bulk-epic">
          <option value="">Set epic…</option>
          ${epics
            .map((e) => `<option value="${escapeHtml(e.id)}">${escapeHtml(e.name)}</option>`)
            .join('')}
          <option value="__none__">No epic</option>
        </select>
      </div>
      <button class="btn btn-ghost btn-sm" id="bulk-clear">Clear</button>
    </div>
    `
        : ''
    }
    <div id="task-list"></div>
  `;

  const listEl = content.querySelector('#task-list') as HTMLElement;
  const filters: Filters = {
    status: 'all',
    assignee: 'all',
    epic: 'all',
    search: '',
  };

  const syncBulkBar = () => {
    const bar = content.querySelector('#bulk-bar');
    if (!bar) return;
    bar.classList.toggle('hidden', selected.size === 0);

    const count = content.querySelector('#bulk-count');
    if (count) count.textContent = `${selected.size} selected`;

    const selectAll = content.querySelector<HTMLInputElement>('#select-all');
    if (selectAll) {
      const visibleIds = Array.from(visible);
      selectAll.checked =
        visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
      selectAll.indeterminate =
        visibleIds.length > 0 &&
        visibleIds.some((id) => selected.has(id)) &&
        !selectAll.checked;
    }
  };

  const applyFilter = () => {
    visible = visibleTaskIds(tasks, filters);
    listEl.innerHTML = renderList(tasks, epics, visible, editable, selected);
    wireList(listEl, ctx, editable, selected, syncBulkBar);
    syncBulkBar();
  };

  content.querySelector('#status-filter')!.addEventListener('change', (e) => {
    filters.status = (e.target as HTMLSelectElement).value;
    applyFilter();
  });
  content.querySelector('#assignee-filter')!.addEventListener('change', (e) => {
    filters.assignee = (e.target as HTMLSelectElement).value;
    applyFilter();
  });
  content.querySelector('#epic-filter')!.addEventListener('change', (e) => {
    filters.epic = (e.target as HTMLSelectElement).value;
    applyFilter();
  });
  content.querySelector('#search')!.addEventListener('input', (e) => {
    filters.search = (e.target as HTMLInputElement).value.trim().toLowerCase();
    applyFilter();
  });

  if (editable) {
    const selectAll = content.querySelector<HTMLInputElement>('#select-all')!;
    selectAll.addEventListener('change', () => {
      const ids = Array.from(visible);
      if (selectAll.checked) ids.forEach((id) => selected.add(id));
      else ids.forEach((id) => selected.delete(id));
      applyFilter();
    });

    content.querySelector('#bulk-clear')!.addEventListener('click', () => {
      selected.clear();
      applyFilter();
    });

    wireBulkActions(content, project, selected, reload);
  }

  content.querySelector('#add-task')?.addEventListener('click', () => {
    openTaskForm(project, { epics, tasks, onChanged: reload });
  });

  applyFilter();
}

function visibleTaskIds(tasks: TaskItem[], f: Filters): Set<string> {
  const matched = new Set<string>();
  for (const t of tasks) {
    if (f.status !== 'all' && t.status !== f.status) continue;
    if (f.assignee !== 'all' && (t.assigneeName || 'Unassigned') !== f.assignee) continue;
    if (f.epic !== 'all' && (t.epicId || 'none') !== f.epic) continue;
    if (f.search && !t.title.toLowerCase().includes(f.search)) continue;
    matched.add(t.id);
  }

  const parentOf = new Map<string, string | null>();
  const childrenOf = new Map<string, string[]>();
  for (const t of tasks) {
    parentOf.set(t.id, t.parentId);
    childrenOf.set(t.id, []);
  }
  for (const t of tasks) {
    if (t.parentId && childrenOf.has(t.parentId)) childrenOf.get(t.parentId)!.push(t.id);
  }

  const visible = new Set(matched);
  for (const id of matched) {
    let p = parentOf.get(id) || null;
    while (p) {
      visible.add(p);
      p = parentOf.get(p) || null;
    }
  }
  const stack = Array.from(matched);
  while (stack.length) {
    const id = stack.pop()!;
    for (const c of childrenOf.get(id) || []) {
      if (!visible.has(c)) {
        visible.add(c);
        stack.push(c);
      }
    }
  }
  return visible;
}

function renderList(
  tasks: TaskItem[],
  epics: Epic[],
  visible: Set<string>,
  editable: boolean,
  selected: Set<string>
): string {
  const filtered = tasks.filter((t) => visible.has(t.id));

  if (filtered.length === 0) {
    return `<div class="empty-state"><h3>No tasks found</h3><p class="muted">${
      editable ? 'Try adjusting your filters, or create a task.' : 'Try adjusting your filters.'
    }</p></div>`;
  }

  const flat = flattenTree(filtered);
  const epicLabel = (id: string | null) =>
    id ? epics.find((e) => e.id === id)?.name || '' : '';

  return `
    <div class="task-list">
      ${flat
        .map(({ task: t, depth }) => {
          const meta = statusMeta(t.status);
          const epic = epicLabel(t.epicId);
          const hasChildren = tasks.some((x) => x.parentId === t.id);
          return `
          <div class="task-row ${editable ? 'has-checkbox' : ''}" data-id="${t.id}">
            ${
              editable
                ? `<input type="checkbox" class="task-checkbox" data-check="${t.id}" ${
                    selected.has(t.id) ? 'checked' : ''
                  } aria-label="Select task" />`
                : ''
            }
            <div class="task-row-title" style="padding-left:${depth * 22}px;">
              ${depth > 0 ? '<span class="muted">↳ </span>' : ''}${escapeHtml(t.title)}
              ${epic ? `<span class="badge" style="color:#7c3aed;background:#f5f3ff;font-size:10px;padding:2px 8px;margin-left:8px;">◆ ${escapeHtml(epic)}</span>` : ''}
              ${hasChildren ? `<span class="muted" style="font-size:11px;margin-left:8px;">· ${tasks.filter((x) => x.parentId === t.id).length} subtasks</span>` : ''}
            </div>
            <span class="assignee">${escapeHtml(t.assigneeName || 'Unassigned')}</span>
            <span class="points-chip">${t.storyPoints} pts</span>
            ${
              editable
                ? `<select class="select-status" data-status="${t.id}">
              ${STATUSES.map(
                (s) =>
                  `<option value="${s.key}" ${t.status === s.key ? 'selected' : ''}>${s.label}</option>`
              ).join('')}
            </select>`
                : `<span class="badge" style="color:${meta.color};background:${meta.bg};justify-self:start;"><span class="dot"></span>${meta.label}</span>`
            }
            <span class="updated muted" style="font-size:12px;">${formatDate(t.updatedAt)}</span>
          </div>
        `;
        })
        .join('')}
    </div>
  `;
}

function wireList(
  listEl: HTMLElement,
  ctx: ProjectContext,
  editable: boolean,
  selected: Set<string>,
  sync: () => void
): void {
  listEl.querySelectorAll<HTMLElement>('.task-row').forEach((row) => {
    const id = row.dataset.id!;
    row.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.select-status') || target.closest('.task-checkbox')) return;
      const task = ctx.tasks.find((t) => t.id === id);
      if (task) {
        openTaskModal(
          task,
          ctx.project,
          ctx.tasks,
          ctx.epics,
          ctx.reload,
          ctx.readOnly,
          ctx.canComment
        );
      }
    });
  });

  if (!editable) return;

  listEl.querySelectorAll<HTMLInputElement>('.task-checkbox').forEach((cb) => {
    cb.addEventListener('click', (e) => e.stopPropagation());
    cb.addEventListener('change', () => {
      const id = cb.dataset.check!;
      if (cb.checked) selected.add(id);
      else selected.delete(id);
      sync();
    });
  });

  listEl.querySelectorAll<HTMLSelectElement>('.select-status').forEach((sel) => {
    sel.addEventListener('change', async (e) => {
      e.stopPropagation();
      const id = sel.dataset.status!;
      const status = sel.value as TaskStatus;
      try {
        await api.patch(`/projects/${ctx.project.id}/tasks/${id}`, { status });
        toast('Status updated');
        ctx.reload();
      } catch (err: any) {
        toast(err.message || 'Could not update status', 'error');
      }
    });
  });
}

function wireBulkActions(
  content: HTMLElement,
  project: ProjectDetail,
  selected: Set<string>,
  reload: () => void
): void {
  const apply = async (
    changes: Record<string, unknown>,
    label: string,
    select: HTMLSelectElement
  ) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    select.disabled = true;
    try {
      const res = await api.patch<{ updated: number }>(
        `/projects/${project.id}/tasks/bulk`,
        { taskIds: ids, changes }
      );
      toast(
        `${res.updated} task${res.updated === 1 ? '' : 's'} ${label}`
      );
      reload();
    } catch (err: any) {
      toast(err.message || 'Could not update tasks', 'error');
      select.value = '';
      select.disabled = false;
    }
  };

  const wire = (
    id: string,
    build: (val: string) => { changes: Record<string, unknown>; label: string } | null
  ) => {
    const select = content.querySelector<HTMLSelectElement>(`#${id}`);
    if (!select) return;
    select.addEventListener('change', () => {
      const val = select.value;
      if (!val) return;
      const action = build(val);
      if (action) void apply(action.changes, action.label, select);
    });
  };

  wire('bulk-sprint', (val) =>
    val === '__none__'
      ? { changes: { sprintId: null }, label: 'removed from sprint' }
      : { changes: { sprintId: val }, label: 'assigned to sprint' }
  );
  wire('bulk-status', (val) => ({
    changes: { status: val },
    label: `moved to ${statusMeta(val).label}`,
  }));
  wire('bulk-assignee', (val) =>
    val === '__none__'
      ? { changes: { assigneeId: null }, label: 'unassigned' }
      : { changes: { assigneeId: val }, label: 'assigned' }
  );
  wire('bulk-epic', (val) =>
    val === '__none__'
      ? { changes: { epicId: null }, label: 'removed from epic' }
      : { changes: { epicId: val }, label: 'moved to epic' }
  );
}
